// CertificateGallery — earned certificates for a learner.
// Mounted on: /learn (self, full), Parent Dashboard (per child, compact),
// NGO child drill-down (compact). Reads university_certificates (the audit
// table all certs land in). The PDF is already generated and stored; this
// component is display + download only.
import { useState, useEffect } from 'react'
import { Award, Download, Loader2 } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'

interface CertRow {
  id: string
  certificate_url: string | null
  issued_at: string
  cert_type: string
  mastery_score: number | null
  issuing_institution?: string | null
  categories: { name: string } | null
}

export default function CertificateGallery({
  userId,
  compact = false,
}: { userId: string; compact?: boolean }) {
  const [certs, setCerts] = useState<CertRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!userId) return
    let cancelled = false
    supabase
      .from('university_certificates')
      .select('id, certificate_url, issued_at, cert_type, mastery_score, issuing_institution, categories!category_id (name)')
      .eq('user_id', userId)
      .order('issued_at', { ascending: false })
      .limit(20)
      .then(({ data }) => {
        if (!cancelled) {
          setCerts((data ?? []) as unknown as CertRow[])
          setLoading(false)
        }
      })
    return () => { cancelled = true }
  }, [userId])

  if (loading) {
    return compact ? null : (
      <div style={{ padding: 24, textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: 13 }}>
        <Loader2 size={16} className="animate-spin" style={{ margin: '0 auto 6px', display: 'block' }} /> Loading certificates…
      </div>
    )
  }

  if (certs.length === 0) return null

  const fmtDate = (d: string) =>
    new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })

  // Compact mode: count + latest + expand hint (for parent/NGO dashboards)
  if (compact) {
    const latest = certs[0]
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
        background: 'rgba(212,168,67,0.06)', border: '1px solid rgba(212,168,67,0.2)',
        borderRadius: 12,
      }}>
        <Award size={18} color="#d4a843" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 800, color: '#d4a843' }}>
            {certs.length} certificate{certs.length === 1 ? '' : 's'} earned
          </span>
          <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.5)', marginLeft: 8 }}>
            Latest: {latest?.categories?.name || 'Course'} · {fmtDate(latest?.issued_at || '')}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
          {latest?.certificate_url && (
            <a href={latest.certificate_url} target="_blank" rel="noopener noreferrer"
              style={{ color: '#d4a843', fontWeight: 700, fontSize: 12, textDecoration: 'none' }}>
              Download →
            </a>
          )}
          <a
            href={`/verify/${latest?.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'rgba(14,165,233,0.8)', fontWeight: 700, fontSize: 12, textDecoration: 'none' }}
          >
            Verify →
          </a>
        </div>
      </div>
    )
  }

  // Full mode: grid of certificate cards (student view)
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', gap: 14 }}>
      {certs.map(cert => (
        <div key={cert.id} style={{
          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(212,168,67,0.2)',
          borderRadius: 16, padding: '20px 18px', display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: 'rgba(212,168,67,0.12)', color: '#d4a843',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Award size={16} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#ffffff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {cert.categories?.name || 'Course Completion'}
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                {fmtDate(cert.issued_at)}
                {cert.mastery_score ? ` · ${cert.mastery_score}% mastery` : ''}
                {cert.issuing_institution ? ` · ${cert.issuing_institution}` : ''}
              </div>
              <a
                href={`/verify/${cert.id}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 10.5, color: 'rgba(14,165,233,0.8)', textDecoration: 'none', fontWeight: 700, marginTop: 2, display: 'inline-block' }}
              >
                Public verification link →
              </a>
            </div>
          </div>
          {cert.certificate_url && (
            <a href={cert.certificate_url} target="_blank" rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '9px 14px', borderRadius: 10,
                background: 'linear-gradient(135deg, #d4a843, #b8912e)',
                color: '#06060c', fontWeight: 800, fontSize: 12.5, textDecoration: 'none',
              }}>
              <Download size={13} /> Download Certificate
            </a>
          )}
        </div>
      ))}
    </div>
  )
}